import { useId, useState, type FormEvent } from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { without } from '@/components/catalog/form-utils';
import { ApiError, api } from '@/lib/api';
import { selectablePickerOptions } from '@/lib/use-deal-pickers';
import type { DealStage, DealType, LeadSource, PickerOption } from '@/lib/types';
import { Button, Checkbox, Field, Input, Modal, Select } from '@/components/ui';

/**
 * Sentinel for the "add new" row. A real option value is a positive integer as
 * a string, so this can never collide with one.
 */
const ADD_NEW = '__add_new__';

export type PickerKind = 'stage' | 'dealType' | 'leadSource';

const COPY: Record<PickerKind, { noun: string; addLabel: string }> = {
  stage: { noun: 'stage', addLabel: '+ Add a new stage…' },
  dealType: { noun: 'deal type', addLabel: '+ Add a new deal type…' },
  leadSource: { noun: 'lead source', addLabel: '+ Add a new lead source…' },
};

export interface PickerFieldProps {
  kind: PickerKind;
  label: string;
  /** The selected id as a string; '' means unassigned. */
  value: string;
  onChange: (value: string) => void;
  options: PickerOption[];
  loading: boolean;
  /**
   * True when THIS list's fetch rejected. Per list, never aggregated: one
   * endpoint really can fail alone, and marking all three would say two lists
   * are unreadable that were read perfectly well.
   */
  failed?: boolean;
  /**
   * The row this deal already holds, for the case where it is missing from
   * `options` entirely — which on a failed fetch is every row. Without it an
   * edit form renders the control blank and reads as "the stage was cleared".
   */
  stored?: { id: number; name: string } | null;
  error?: string;
  /** Hands the created row back so the shared option list can absorb it. */
  onCreated: (row: DealStage | DealType | LeadSource) => void;
}

/**
 * A picker whose options are rows in a table, not values in an enum. The
 * dropdown's "add new" row posts a real record and selects it, so a missing
 * option needs no migration.
 *
 * Retired options are hidden from new selections but kept and marked when
 * they are the record's own value — dropping one would reassign the record
 * on the next save.
 *
 * ⚠️ **`failed` SUPPRESSES the "add new" row, and that is the point of the
 * prop.** A read failure used to leave an empty dropdown sitting above its
 * own "+ Add a new…" option — pressing it POSTs into a `UNIQUE`-named table,
 * duplicating a row that is invisible precisely because the list didn't
 * load. Offering creation anyway was rejected: the option can be added after
 * a retry, but a duplicate stage becomes two kanban columns meaning one
 * thing and cannot be merged back.
 *
 * 🔴 **The suppression is now SILENT.** It used to be announced by a warning
 * beside the control; that sentence was removed on the owner's stricter
 * no-helper-text ruling (2026-09-05), so on a failed read the dropdown is
 * empty and "+ Add a new…" is simply absent, with nothing saying why. The
 * suppression itself is unchanged and must stay — it is what stops a duplicate
 * row being POSTed into a `UNIQUE`-named table over an unread list.
 */
export function PickerField({
  kind,
  label,
  value,
  onChange,
  options,
  loading,
  failed = false,
  stored = null,
  error,
  onCreated,
}: PickerFieldProps) {
  const [creating, setCreating] = useState(false);

  const currentId = value === '' ? null : Number(value);
  const selectable = selectablePickerOptions(options, currentId);
  const copy = COPY[kind];

  /*
   * ⚠️ Deliberately NOT labelled "(retired)": reaching this branch means the
   * row is absent from the response (fetch failed, or row deleted) — a
   * retired row still comes back and is already marked by
   * `selectablePickerOptions`. Absence is not evidence of retirement.
   */
  const missingStored =
    stored !== null && stored.id === currentId && !selectable.some((row) => row.id === stored.id)
      ? stored
      : null;

  return (
    <>
      <Field label={label} error={error}>
        {({ id, 'aria-describedby': describedBy }) => (
          <Select
            id={id}
            value={value}
            disabled={loading}
            invalid={Boolean(error)}
            aria-describedby={describedBy}
            onChange={(event) => {
              if (event.target.value === ADD_NEW) {
                // Leave the field as it was: the sentinel must never survive as
                // a value, and the modal may be cancelled.
                setCreating(true);
                return;
              }
              onChange(event.target.value);
            }}
          >
            <option value="">{loading ? 'Loading…' : 'Unassigned'}</option>
            {missingStored && <option value={missingStored.id}>{missingStored.name}</option>}
            {selectable.map((option) => (
              <option key={option.id} value={option.id}>
                {option.isActive ? option.name : `${option.name} (retired)`}
              </option>
            ))}
            {!loading && !failed && <option value={ADD_NEW}>{copy.addLabel}</option>}
          </Select>
        )}
      </Field>

      {creating && (
        <PickerCreateModal
          kind={kind}
          onClose={() => setCreating(false)}
          onCreated={(row) => {
            onCreated(row);
            onChange(String(row.id));
            setCreating(false);
          }}
        />
      )}
    </>
  );
}

interface CreateValues {
  name: string;
  probability: string;
  sortOrder: string;
  isWon: boolean;
  isLost: boolean;
}

const EMPTY: CreateValues = {
  name: '',
  probability: '',
  sortOrder: '',
  isWon: false,
  isLost: false,
};

/**
 * The `path` values a 422 may name that this dialog can actually MARK.
 *
 * ⚠️ These are the keys it SENDS — derived from `EMPTY` so the two cannot
 * drift. The schemas are `.strict()`, so a key outside this set is something
 * this dialog does not render; routing it by path would mark nothing while
 * still counting toward "fields need attention".
 */
const KNOWN_WRITE_KEYS: readonly string[] = Object.keys(EMPTY);

/**
 * The "add an option" dialog.
 *
 * Deliberately minimal: name, and the couple of fields that cannot be guessed.
 * `isActive` is not offered — nobody creates an option in order to retire it,
 * and the server defaults it to true. Editing and retiring live in an admin
 * screen, not in the middle of writing a deal.
 */
function PickerCreateModal({
  kind,
  onClose,
  onCreated,
}: {
  kind: PickerKind;
  onClose: () => void;
  onCreated: (row: DealStage | DealType | LeadSource) => void;
}) {
  const formId = useId();
  const copy = COPY[kind];
  const [values, setValues] = useState<CreateValues>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function setField(key: keyof CreateValues, next: string | boolean) {
    setValues((prev) => ({ ...prev, [key]: next }));
    setErrors((prev) => (key in prev ? without(prev, key) : prev));
  }

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};
    const name = values.name.trim();
    if (name === '') found.name = 'Name is required.';
    else if (name.length > 120) found.name = 'Must be 120 characters or fewer.';

    const sortOrder = values.sortOrder.trim();
    // Negatives are allowed here, unlike everywhere else in the app.
    if (sortOrder !== '' && !/^-?\d+$/.test(sortOrder)) {
      found.sortOrder = 'Enter a whole number.';
    }

    if (kind === 'stage') {
      const probability = values.probability.trim();
      if (probability !== '') {
        if (!/^\d+$/.test(probability)) found.probability = 'Enter a whole number from 0 to 100.';
        else if (Number(probability) > 100) found.probability = 'Cannot be more than 100.';
      }
    }
    return found;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    /*
     * Portalled to the body, but still a child of the deal form in the React
     * tree — React propagates by the React tree, not the DOM. Without this,
     * submitting "add a stage" also submits the deal behind it half-filled.
     */
    event.stopPropagation();
    const found = validate();
    setErrors(found);
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    /*
     * Real JSON numbers: the picker schemas use plain `z.number()` for
     * sortOrder, so a numeric string 422s here even though the deals
     * endpoints coerce it. No actor id sent — the server reads the bearer
     * token, and the schemas are `.strict()`.
     */
    const base = {
      name: values.name.trim(),
      ...(values.sortOrder.trim() !== '' ? { sortOrder: Number(values.sortOrder) } : {}),
    };

    setSubmitting(true);
    try {
      const result =
        kind === 'stage'
          ? await api.createDealStage({
              ...base,
              ...(values.probability.trim() !== ''
                ? { probability: Number(values.probability) }
                : {}),
              ...(values.isWon ? { isWon: true } : {}),
              ...(values.isLost ? { isLost: true } : {}),
            })
          : kind === 'dealType'
            ? await api.createDealType(base)
            : await api.createLeadSource(base);
      onCreated(result.data);
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === 409) {
          // `name` is UNIQUE, and the message names the collision.
          setErrors({ name: caught.message });
        } else {
          /*
           * Split against this dialog's OWN field list, not the shape of the
           * path. `unroutable` is anything no input here can carry — an
           * EMPTY-path `.strict()` refusal, or a path naming an unrendered
           * key — rendered at form level, never counted as highlighted.
           */
          const { fields, unroutable } = caught.routeFieldErrors(KNOWN_WRITE_KEYS);
          setErrors(fields);
          if (unroutable.length > 0) setFormError(unroutable.join(' '));
          else if (Object.keys(fields).length === 0) setFormError(caught.message);
        }
      } else {
        setFormError('Could not reach the server. Check your connection and try again.');
      }
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      dismissOnBackdrop={false}
      title={`New ${copy.noun}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={values.name.trim() === ''}
          >
            Add {copy.noun}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-3">
        <FormErrorSummary formError={formError} fieldErrorCount={Object.keys(errors).length} />

        <Field label="Name" required error={errors.name}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              autoFocus
              value={values.name}
              maxLength={120}
              invalid={Boolean(errors.name)}
              aria-describedby={describedBy}
              onChange={(event) => setField('name', event.target.value)}
            />
          )}
        </Field>

        {kind === 'stage' && (
          <Field label="Probability" error={errors.probability}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                inputMode="numeric"
                value={values.probability}
                invalid={Boolean(errors.probability)}
                aria-describedby={describedBy}
                onChange={(event) => setField('probability', event.target.value)}
              />
            )}
          </Field>
        )}

        <Field label="Sort order" error={errors.sortOrder}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              inputMode="numeric"
              value={values.sortOrder}
              placeholder="0"
              invalid={Boolean(errors.sortOrder)}
              aria-describedby={describedBy}
              onChange={(event) => setField('sortOrder', event.target.value)}
            />
          )}
        </Field>

        {kind === 'stage' && (
          <fieldset className="grid gap-2">
            <legend className="text-xs font-medium text-fg-secondary">
              Closes the deal
            </legend>
            {/* Independent flags rather than a state machine — the API allows
                neither, either, or in principle both. */}
            <Checkbox
              label="Counts as won"
              checked={values.isWon}
              onChange={(event) => setField('isWon', event.target.checked)}
            />
            <Checkbox
              label="Counts as lost"
              checked={values.isLost}
              onChange={(event) => setField('isLost', event.target.checked)}
            />
          </fieldset>
        )}
      </form>
    </Modal>
  );
}
